// Polygon-based surface segments produced by AI detection.
// Pure utilities — no React, no AI, no I/O. Used by PaintCanvas and the
// preview engine to hit-test and rasterise masks for instant recoloring.

import type { SurfaceKind } from "../studio";

export type Polygon = [number, number][]; // [[x,y], ...] in normalized 0..1 image coords

export type Segment = {
  id: string;
  kind: SurfaceKind;
  label: string;
  // A surface can span multiple disjoint regions (e.g. two visible walls).
  polygons: Polygon[];
  /** 0..1 detector confidence. Optional — older records may not have it. */
  confidence?: number;
  /** Review status derived from confidence + validation. Optional. */
  reviewStatus?: "high" | "review" | "low";
  /** Which pipeline pass produced this segment. Optional (diagnostics only). */
  source?: "structural" | "trim" | "manual";
};

export function pointInPolygon(x: number, y: number, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function pointInSegment(seg: Segment, x: number, y: number): boolean {
  for (const poly of seg.polygons) if (pointInPolygon(x, y, poly)) return true;
  return false;
}

function polyArea(poly: Polygon): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  }
  return Math.abs(a / 2);
}

/** Topmost (smallest) segment under the point, or null. */
export function hitTest(segments: Segment[], x: number, y: number): Segment | null {
  let hit: Segment | null = null;
  let bestArea = Infinity;
  for (const seg of segments) {
    if (!pointInSegment(seg, x, y)) continue;
    const a = seg.polygons.reduce((s, p) => s + polyArea(p), 0);
    if (a < bestArea) { bestArea = a; hit = seg; }
  }
  return hit;
}

/** Rasterise all polygons of a segment into a single-channel mask. */
export function rasterizeSegment(seg: Segment, w: number, h: number): Uint8ClampedArray {
  const mask = new Uint8ClampedArray(w * h);
  for (const poly of seg.polygons) {
    const pts = poly.map(([x, y]) => ({ x: x * w, y: y * h }));
    fillPolygonInto(mask, w, h, pts);
  }
  return mask;
}

function fillPolygonInto(mask: Uint8ClampedArray, w: number, h: number, pts: { x: number; y: number }[]) {
  if (pts.length < 3) return;
  let minY = Infinity, maxY = -Infinity;
  for (const p of pts) { if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y; }
  minY = Math.max(0, Math.floor(minY));
  maxY = Math.min(h - 1, Math.ceil(maxY));
  const n = pts.length;
  for (let y = minY; y <= maxY; y++) {
    const xs: number[] = [];
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const pi = pts[i], pj = pts[j];
      if ((pi.y <= y && pj.y > y) || (pj.y <= y && pi.y > y)) {
        const t = (y - pi.y) / (pj.y - pi.y);
        xs.push(pi.x + t * (pj.x - pi.x));
      }
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = Math.max(0, Math.ceil(xs[k]));
      const x1 = Math.min(w - 1, Math.floor(xs[k + 1]));
      for (let x = x0; x <= x1; x++) mask[y * w + x] = 255;
    }
  }
}

/** Stroke a segment's outline on a 2D canvas in pixel coords. */
export function strokeSegment(
  ctx: CanvasRenderingContext2D, seg: Segment, w: number, h: number,
) {
  ctx.beginPath();
  for (const poly of seg.polygons) {
    poly.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x * w, y * h) : ctx.lineTo(x * w, y * h)));
    ctx.closePath();
  }
  ctx.stroke();
}

export function fillSegment(
  ctx: CanvasRenderingContext2D, seg: Segment, w: number, h: number,
) {
  ctx.beginPath();
  for (const poly of seg.polygons) {
    poly.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x * w, y * h) : ctx.lineTo(x * w, y * h)));
    ctx.closePath();
  }
  ctx.fill();
}